import React, { useMemo } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { motion, useReducedMotion } from "framer-motion";
import BikeImage from "../../stuff/bike.png";

const PRIMARY = "#0c513f";
const ACCENT = "#ffbf07";
const CREAM = "#fdf7e9";

function Section7() {
  const reduceMotion = useReducedMotion();

  const variants = useMemo(() => {
    if (reduceMotion) {
      return {
        left: { hidden: { opacity: 1 }, show: { opacity: 1 } },
        right: { hidden: { opacity: 1 }, show: { opacity: 1 } },
        pop: { hidden: { opacity: 1 }, show: { opacity: 1 } },
      };
    }
    return {
      left: {
        hidden: { opacity: 0, x: -26, y: 8 },
        show: { opacity: 1, x: 0, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
      },
      right: {
        hidden: { opacity: 0, x: 26, y: 8 },
        show: { opacity: 1, x: 0, y: 0, transition: { duration: 0.7, ease: "easeOut", delay: 0.08 } },
      },
      pop: {
        hidden: { opacity: 0, scale: 0.98 },
        show: { opacity: 1, scale: 1, transition: { duration: 0.55, ease: "easeOut", delay: 0.18 } },
      },
    };
  }, [reduceMotion]);

  return (
    <section
      className="contact_section"
      style={{
        position: "relative",
        overflow: "hidden",
        padding: "90px 0",
        background: `linear-gradient(180deg, ${CREAM}, #ffffff)`,
        borderTop: "1px solid rgba(255,191,7,0.22)",
      }}
    >
      {/* soft blobs */}
      <div aria-hidden="true" style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        <div
          style={{
            position: "absolute",
            top: "-220px",
            right: "-240px",
            width: 620,
            height: 620,
            borderRadius: 999,
            background: "rgba(255,191,7,0.18)",
            filter: "blur(90px)",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "-240px",
            left: "-260px",
            width: 680,
            height: 680,
            borderRadius: 999,
            background: "rgba(12,81,63,0.10)",
            filter: "blur(100px)",
          }}
        />
      </div>

      <Container style={{ position: "relative", zIndex: 2 }}>
        <Row className="justify-content-center align-items-center g-4">
          {/* Image Column */}
          <Col xs={12} md={6} className="d-flex justify-content-center">
            <motion.div
              variants={variants.left}
              initial="hidden"
              whileInView="show"
              viewport={{ once: false, amount: 0.3 }}
              style={{
                position: "relative",
                width: "min(520px, 100%)",
                padding: "12px 10px",
              }}
            >
              {/* motion lines behind bike (simple + sleek) */}
              {!reduceMotion && (
                <>
                  <motion.div
                    aria-hidden="true"
                    animate={{ x: [0, 10, 0], opacity: [0.18, 0.28, 0.18] }}
                    transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
                    style={{
                      position: "absolute",
                      left: "6%",
                      top: "34%",
                      width: "62%",
                      height: 10,
                      borderRadius: 999,
                      background: "rgba(12,81,63,0.12)",
                      filter: "blur(0.4px)",
                    }}
                  />
                  <motion.div
                    aria-hidden="true"
                    animate={{ x: [0, 14, 0], opacity: [0.14, 0.24, 0.14] }}
                    transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
                    style={{
                      position: "absolute",
                      left: "10%",
                      top: "45%",
                      width: "52%",
                      height: 8,
                      borderRadius: 999,
                      background: "rgba(255,191,7,0.18)",
                    }}
                  />
                  <motion.div
                    aria-hidden="true"
                    animate={{ x: [0, 8, 0], opacity: [0.12, 0.20, 0.12] }}
                    transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
                    style={{
                      position: "absolute",
                      left: "14%",
                      top: "56%",
                      width: "42%",
                      height: 7,
                      borderRadius: 999,
                      background: "rgba(12,81,63,0.10)",
                    }}
                  />
                </>
              )}

              {/* bike card */}
              <motion.div
                variants={variants.pop}
                initial="hidden"
                whileInView="show"
                viewport={{ once: false, amount: 0.3 }}
                whileHover={reduceMotion ? undefined : { y: -6, rotate: -0.2 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
                style={{
                  borderRadius: 26,
                  background: "rgba(255,255,255,0.75)",
                  border: "1px solid rgba(255,191,7,0.26)",
                  boxShadow: "0 22px 70px rgba(0,0,0,0.10)",
                  backdropFilter: "blur(12px)",
                  WebkitBackdropFilter: "blur(12px)",
                  padding: "16px 14px",
                }}
              >
                <div
                  aria-hidden="true"
                  style={{
                    height: 6,
                    borderRadius: 999,
                    background: `linear-gradient(90deg, ${ACCENT}, rgba(255,191,7,0.12), ${ACCENT})`,
                    opacity: 0.9,
                    marginBottom: 12,
                  }}
                />
                <img
                  src={BikeImage}
                  className="img-fluid"
                  alt="Delivery Bike"
                  style={{ width: "100%", height: "auto", display: "block" }}
                />
              </motion.div>
            </motion.div>
          </Col>

          {/* Text Column */}
          <Col xs={12} md={6} className="d-flex justify-content-center">
            <motion.div
              variants={variants.right}
              initial="hidden"
              whileInView="show"
              viewport={{ once: false, amount: 0.3 }}
              style={{ width: "min(520px, 100%)" }}
            >
              <div
                style={{
                  borderRadius: 26,
                  background: "rgba(255,255,255,0.78)",
                  border: "1px solid rgba(12,81,63,0.12)",
                  boxShadow: "0 22px 70px rgba(0,0,0,0.10)",
                  backdropFilter: "blur(12px)",
                  WebkitBackdropFilter: "blur(12px)",
                  overflow: "hidden",
                }}
              >
                {/* accent top */}
                <div
                  aria-hidden="true"
                  style={{
                    height: 7,
                    background: `linear-gradient(90deg, ${PRIMARY}, ${ACCENT}, ${PRIMARY})`,
                    opacity: 0.95,
                  }}
                />

                <div style={{ padding: 22 }}>
                  {/* tiny pill */}
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "10px 14px",
                      borderRadius: 999,
                      background: "rgba(12,81,63,0.06)",
                      border: "1px solid rgba(12,81,63,0.10)",
                      marginBottom: 14,
                    }}
                  >
                    <span
                      style={{
                        width: 9,
                        height: 9,
                        borderRadius: 999,
                        background: ACCENT,
                        boxShadow: "0 0 16px rgba(255,191,7,0.55)",
                      }}
                    />
                  
                  </div>

                  <h3
                    style={{
                      margin: 0,
                      color: "#101010",
                      fontSize: "clamp(24px, 2.5vw, 34px)",
                      lineHeight: 1.1,
                      letterSpacing: "-0.3px",
                    }}
                  >
                    Hot food. Swift delivery.
                  </h3>

                  <p
                    style={{
                      marginTop: 12,
                      marginBottom: 14,
                      fontFamily: "Kadwa, sans-serif",
                      color: "rgba(16,16,16,0.66)",
                      lineHeight: 1.9,
                      fontSize: 14.6,
                    }}
                  >
                    Your order moves like it has somewhere to be — quick pickup, smooth handoff, and fast arrival.
                    We’re built to keep your meal hot, fresh, and on-time.
                  </p>

                  {/* stats chips */}
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 10,
                      marginTop: 10,
                    }}
                  >
                    {[
                      { t: "Hot & fresh", b: "No long waits" },
                      { t: "Fast handoff", b: "Smooth delivery flow" },
                      { t: "Reliable ETA", b: "Consistent timing" },
                    ].map((c) => (
                      <div
                        key={c.t}
                        style={{
                          flex: "1 1 160px",
                          padding: "12px 12px",
                          borderRadius: 18,
                          background: "rgba(255,191,7,0.10)",
                          border: "1px solid rgba(255,191,7,0.26)",
                        }}
                      >
                        <div style={{ fontWeight: 900, color: PRIMARY, fontSize: 13 }}>{c.t}</div>
                        <div style={{ marginTop: 4, color: "rgba(16,16,16,0.60)", fontSize: 12.8 }}>
                          {c.b}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* underline accent */}
                  <div
                    aria-hidden="true"
                    style={{
                      height: 4,
                      width: 54,
                      backgroundColor: ACCENT,
                      borderRadius: 999,
                      marginTop: 18,
                    }}
                  />
                </div>
              </div>
            </motion.div>
          </Col>
        </Row>
      </Container>
    </section>
  );
}

export default Section7;
